import { Dropdown, Segment } from '@mairistem/composants';
import {
  Liste,
  ListeTable,
  ListeTableActionCsv,
  ListeTableActionPrint,
  ListeTableContainer,
} from '@mairistem/liste';
import _ from 'lodash';
import React, { type ReactNode } from 'react';

import { CycleContext, FactureRejetContext, LoadingContext } from '@/contexts';
import type { FactureListePrelevementItemType } from '@/types/facture';
import { formatFacturesToPrelevementListeItem } from '@/utils/facture';
import RejetsListeTableRow from './RejetsListeTableRow';

import './PrelevementsListe.less';

interface RejetsListeProps {
  selectedCycle: number;
  loading?: boolean;
  onCycleSelect?: (value: number) => void;
  onRejetDelete?: (rejet: FactureListePrelevementItemType) => void;
}

const RejetsListe = (props: RejetsListeProps) => {
  const { selectedCycle, loading, onCycleSelect, onRejetDelete } = props;

  const { total, factures, search, setSearch, setPage } = React.useContext(FactureRejetContext);

  const { cycles } = React.useContext(CycleContext);

  const {
    facturesLoadings: { factures: facturesLoading },
  } = React.useContext(LoadingContext);

  const formatedFactures = React.useMemo<FactureListePrelevementItemType[]>(
    () => formatFacturesToPrelevementListeItem(factures),
    [JSON.stringify(factures)],
  );

  const handleSearch = React.useCallback(
    async (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const handleCycleSelect = React.useCallback(
    (e, data) => {
      onCycleSelect?.(data.value);
    },
    [onCycleSelect],
  );

  const renderRow = (item, __, rowProps) => <RejetsListeTableRow item={item} rowProps={rowProps} />;

  const cyclesOptions = React.useMemo(
    () =>
      _.map(_.orderBy(cycles, 'titre'), (cycle) => ({
        key: cycle.identifiant,
        value: cycle.identifiant,
        text: cycle.titre,
      })),
    [cycles],
  );

  return (
    <Segment className="liste-prelevements" basic style={{ margin: 0, padding: 0, height: '100%' }}>
      <Liste
        title=""
        loading={loading || facturesLoading || _.isNil(factures)}
        onSearch={handleSearch}
        searchable
        search={{
          value: search,
          placeholder: 'Rechercher un titulaire de compte ou un cycle de facturation',
          clearable: true,
          autoFocus: true,
        }}
      >
        <Liste.Header>
          <Dropdown
            selection
            options={[
              {
                key: 'all',
                value: 0,
                text: 'Tous les cycles',
              },
              ...cyclesOptions,
            ]}
            defaultValue={0}
            value={selectedCycle}
            onChange={handleCycleSelect}
            style={{ width: 250 }}
          />
        </Liste.Header>
        <Liste.Content>
          <div style={{ height: 450 }}>
            <ListeTableContainer
              items={formatedFactures}
              onDeleteClick={onRejetDelete}
              allowDeletions
            >
              <ListeTable
                count={_.isEmpty(formatedFactures) ? 0 : total}
                loading={loading || facturesLoading || _.isNil(factures)}
                perPage={30}
                onLoad={handleLoad}
              >
                {/* Pas dans un composant à part pour ce Header là
                car bug taille des colonnes avec ListeTable.Actions */}
                <ListeTable.Header>
                  <ListeTable.Column name="numero" verticalAlign="middle" width={2}>
                    N° de facture
                  </ListeTable.Column>
                  <ListeTable.Column
                    name="montant"
                    verticalAlign="middle"
                    textAlign="right"
                    width={1}
                  >
                    Montant
                  </ListeTable.Column>
                  <ListeTable.Column name="cycle" verticalAlign="middle" width={4}>
                    Cycle de facturation
                  </ListeTable.Column>
                  <ListeTable.Column name="titulaireComptePreleve" verticalAlign="middle" width={2}>
                    Titulaire du compte
                  </ListeTable.Column>
                  <ListeTable.Column name="compteBancairePreleve" verticalAlign="middle" width={3}>
                    Compte bancaire
                  </ListeTable.Column>
                  <ListeTable.Column name="dateRejet" verticalAlign="middle" width={2}>
                    Date de rejet
                  </ListeTable.Column>
                  <ListeTable.Column name="motifRejet" verticalAlign="middle" width={2}>
                    Motif de rejet
                  </ListeTable.Column>
                </ListeTable.Header>
                {renderRow as unknown as ReactNode}
                <ListeTable.Actions>
                  <ListeTableActionCsv
                    items={formatedFactures}
                    columns={[
                      'numero',
                      'montant',
                      'cycle',
                      'titulaireComptePreleve',
                      'compteBancairePreleve',
                      'dateRejet',
                      'motifRejet',
                    ]}
                    options={{ filename: 'Liste des prélèvements rejetés' }}
                  />
                  <ListeTableActionPrint
                    printOptions={{ documentName: 'Liste des prélèvements rejetés' }}
                  />
                </ListeTable.Actions>
              </ListeTable>
            </ListeTableContainer>
          </div>
        </Liste.Content>
      </Liste>
    </Segment>
  );
};

export default RejetsListe;
